import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FieldPath,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';

import { Accordion } from '@radix-ui/react-accordion';
import {
  BoxIcon,
  Building2,
  CircleDollarSignIcon,
  NotebookIcon,
  Package,
  WarehouseIcon,
  Weight,
} from 'lucide-react';

import { Button } from 'components/Button';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useGuardedCreateLoad } from 'hooks/useGuardedCreateLoad';
import { useLoadContext } from 'hooks/useLoadContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { getCommodities } from 'lib/api/getCommodities';
import { getCustomers } from 'lib/api/getCustomers';
import {
  type AccessorialLookupItem,
  type EquipmentMappingItem,
  type LookupItem,
  getBootstrapGlobalTranz,
} from 'lib/api/getEquipmentLookupsGlobalTranz';
import { getLocations } from 'lib/api/getLocations';
import { updateTMS } from 'lib/api/updateTMS';
import { FormStorageService } from 'lib/services/FormStorage/service';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import { NotesSectionForm } from 'pages/LoadView/LoadInformation/Sections/GlobalTranzTMS/NotesSectionForm';
import LoadBuildingAccordionControls from 'pages/QuoteView/LoadBuilding/components/LoadBuildingAccordionControls';
import { LoadCreatedMessage } from 'pages/QuoteView/LoadBuilding/components/LoadCreatedMessage';
import { useLoadBuildingAccordionTabs } from 'pages/QuoteView/LoadBuilding/hooks/useLoadBuildingAccordionTabs';
import { suggestedTransportTypeOrEmpty } from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import {
  AdditionalReference,
  Commodity,
  CompanyCoreInfo,
  CostLineItem,
  Customer,
  Dropoff,
  Load,
  LoadCarrier,
  LoadStopTypes,
  NormalizedLoad,
  Note,
  Pickup,
  RateData,
  TMSCommodity,
  TMSCustomer,
  TMSLocation,
  createInitLoad,
  createInitRateData,
  createInitSpecs,
  normalizeLoad,
} from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMSOperation } from 'types/api/UpdateTMS';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { SuggestedLoad } from 'types/suggestions/LoadBuildingSuggestions';
import {
  acceptLoadViewSuggestion,
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  ManualFieldError,
  applyManualValidationErrors,
  handleFormErrors,
  loadBuildingManualValidationDisplayOptions,
  scheduleLoadBuildingSuggestionValidation,
} from 'utils/formValidation';
import {
  injectSelectedObject,
  normalizeToArray,
  normalizeToStringArray,
  withLoadBuildingSuggestionContext,
} from 'utils/loadInfoAndBuilding';
import { denormalizeDatesForTMSForm } from 'utils/parseDatesForTMSForm';

import {
  validateDeliveryOnOrAfterPickupDate,
  validateDuplicateStopLocations,
} from '../utils/stopValidation';
import { AccessorialsForm } from './Accessorials';
import { CustomerSectionForm } from './Customer';
import { FinancialsForm } from './Financials';
import { ShipmentItems } from './ShipmentItems';
import { SpecificationsForm } from './Specifications';
import { StopForm } from './Stop';
import {
  getDefaultTimes,
  handleCommodityOrLocationFetchError,
  mapCommodityDefaults,
  normalizeGlobalTranzTransportType,
  toHHMMString,
  withCommodityTotalWeight,
} from './utils';

enum AvailableTabs {
  customer = 'customer',
  billTo = 'billTo',
  specifications = 'specifications',
  shipmentItems = 'shipmentItems',
  pickup = 'pickup',
  consignee = 'consignee',
  accessorials = 'accessorials',
  carrier = 'carrier',
  rates = 'rates',
  notes = 'notes',
}

// Helper function to create a note object from pickup instructions
const createNoteFromInstructions = (instructions: string): Note => ({
  note: instructions.trim(),
  updatedBy: '',
  createdAt: new Date().toISOString(),
  isException: null,
  isOnTime: null,
  source: 'form',
});

type LoadBuildingTextInputProps = React.ComponentPropsWithoutRef<
  typeof RHFTextInput
> & { name: FieldPath<NormalizedLoad> };

export function LoadBuildingTextInput(props: LoadBuildingTextInputProps) {
  return <RHFTextInput {...props} />;
}

const initLoadBuildingForm = (tmsName: string): NormalizedLoad => {
  // Create default base rate line item (accessorialId 1 = Base Rate per GT API)
  const baseRateLineItem: CostLineItem = {
    label: 'Base Rate',
    unitBasis: 'Flat',
    quantity: 1,
    ratePerUnitUSD: 0,
    totalChargeUSD: 0,
    note: '',
    accessorialId: 1,
  };

  // Default pickup date = 1 day ahead (tomorrow); delivery date = same so delivery >= pickup
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  const tomorrowISO = tomorrow.toISOString();

  // Default dock hours (HH:MM) so openTime/closeTime are always in form state and sent to BE
  const defaultPickupTimes = getDefaultTimes(LoadStopTypes.Pickup);
  const defaultConsigneeTimes = getDefaultTimes(LoadStopTypes.Consignee);

  const initLoad = createInitLoad();
  const normalized = normalizeLoad(tmsName, {
    ...initLoad,
    mode: 'TL',
    declaredValueUSD: 100000, // Default Total Shipment Value (USD)
    pickup: {
      ...initLoad.pickup,
      readyTime: tomorrowISO,
      openTime: defaultPickupTimes.openTime,
      closeTime: defaultPickupTimes.closeTime,
    } as Pickup,
    consignee: {
      ...initLoad.consignee,
      mustDeliver: tomorrowISO,
      openTime: defaultConsigneeTimes.openTime,
      closeTime: defaultConsigneeTimes.closeTime,
    } as Dropoff,
    specifications: {
      ...createInitSpecs(),
      transportType: 'Van',
      serviceType: 'TL', // Default to TL for GlobalTranz create load
      service: '1', // Default to Full for GlobalTranz Specs > Service (string for API)
      shipmentType: 'Spot Quote',
    },
    rateData: {
      ...createInitRateData(),
      // Initialize with default Base Rate row - clone to avoid shared reference
      customerLineItems: [{ ...baseRateLineItem }],
      carrierLineItems: [{ ...baseRateLineItem }],
    },
  });

  return {
    ...normalized,
    commodities: mapCommodityDefaults(normalized.commodities ?? []),
  };
};

interface UnifiedGlobalTranzFormProps {
  isCreateMode: boolean;
  load?: Load;
  onSubmit?: (data: NormalizedLoad) => void;
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
  externalLinks?: any;
}

export default function UnifiedGlobalTranzForm({
  isCreateMode,
  load,
  onSubmit,
  onSuccess,
  onError,
}: UnifiedGlobalTranzFormProps) {
  const tmsName = TMS.GlobalTranzTMS;
  const { invalidateLoad } = useLoadContext();
  const { tmsIntegrations } = useServiceFeatures();
  const firstTmsIntegrationID =
    tmsIntegrations && tmsIntegrations.length > 0
      ? tmsIntegrations[0].id
      : undefined;
  const tmsID = isCreateMode ? firstTmsIntegrationID : load?.tmsID;

  const { toast } = useToast();
  const guardedCreateLoad = useGuardedCreateLoad();
  const [isLoading, setIsLoading] = useState(false);

  // Only sections rendered in the accordion (billTo/carrier/notes are not).
  const allTabs = useMemo(() => {
    const tabs = [
      AvailableTabs.customer,
      AvailableTabs.specifications,
      AvailableTabs.shipmentItems,
      AvailableTabs.rates,
      AvailableTabs.pickup,
      AvailableTabs.consignee,
      AvailableTabs.accessorials,
    ];
    if (!isCreateMode) {
      tabs.push(AvailableTabs.notes);
    }
    return tabs;
  }, [isCreateMode]);

  const {
    activeTabs,
    setActiveTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
  } = useLoadBuildingAccordionTabs({ allTabs });
  const [isLoadBuildingSuggestionClicked, setIsLoadBuildingSuggestionClicked] =
    useState(false);
  const [showAIHints, setShowAIHints] = useState(false);

  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const [isLoadingCommodities, setIsLoadingCommodities] = useState(true);
  const [customers, setCustomers] = useState<Maybe<TMSCustomer[]>>(null);
  const [locations, setLocations] = useState<Maybe<TMSLocation[]>>(null);
  const [commodities, setCommodities] = useState<Maybe<TMSCommodity[]>>(null);
  // Carrier section (CarrierSectionForm / BillToSectionForm) was removed; can be re-integrated in future if needed.
  const [builtLoad, setBuiltLoad] = useState<Maybe<Load>>(null);

  // GTZ bootstrap data (equipment types, mapping, lengths, display fields, accessorials)
  const [equipmentTypes, setEquipmentTypes] = useState<LookupItem[]>([]);
  const [equipmentMapping, setEquipmentMapping] = useState<
    EquipmentMappingItem[]
  >([]);
  const [equipmentLengths, setEquipmentLengths] = useState<LookupItem[]>([]);
  const [displayFields, setDisplayFields] = useState<LookupItem[]>([]);
  const [accessorials, setAccessorials] = useState<AccessorialLookupItem[]>([]);
  const [_isLoadingBootstrap, setIsLoadingBootstrap] = useState(true);
  const [isRefreshingBootstrap, setIsRefreshingBootstrap] = useState(false);

  const {
    currentState: {
      clickedSuggestion,
      threadId,
      emailId,
      threadItemId,
      goToSuggestion,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const isMountedRef = useRef(true);
  const prevCustomerBKRef = useRef<Undef<string>>(undefined);
  const currentRequestLocationCustomerExternalTmsIdRef =
    useRef<Undef<string>>(undefined);
  const currentCommodityRequestRef = useRef<{
    customerID?: number;
    carrierType?: number;
  }>({});

  const fetchCustomers = async () => {
    if (!tmsID) return;

    setIsLoadingCustomers(true);

    const res = await getCustomers(tmsID);

    if (!isMountedRef.current) {
      return; // Return immediately without any state updates
    }

    if (res.isOk()) {
      setCustomers(res.value.customerList);
      setIsLoadingCustomers(false);
      return;
    }
    toast({
      description: res.error.message || 'Error while fetching customer list.',
      variant: 'destructive',
    });
    setIsLoadingCustomers(false);
  };

  const fetchBootstrap = useCallback(
    async (hardRefresh: boolean) => {
      if (!tmsID) return;

      if (hardRefresh) {
        setIsRefreshingBootstrap(true);
      } else {
        setIsLoadingBootstrap(true);
      }

      try {
        const res = await getBootstrapGlobalTranz(tmsID, {
          quoteModeTypeEnum: 3,
          hardRefresh,
        });

        if (res.isOk()) {
          setEquipmentTypes(res.value.equipmentTypes);
          setEquipmentMapping(res.value.equipmentMapping);
          setEquipmentLengths(res.value.equipmentLengths);
          setDisplayFields(res.value.displayFields);
          setAccessorials(res.value.accessorials);
          if (hardRefresh) {
            toast({
              description: 'Reference data refreshed from GlobalTranz.',
              variant: 'success',
            });
          }
        } else {
          toast({
            description:
              res.error.message || 'Failed to fetch GTZ reference data',
            variant: 'destructive',
          });
        }
      } catch (err) {
        toast({
          description: 'Failed to fetch GTZ reference data',
          variant: 'destructive',
        });
        throw err;
      } finally {
        if (hardRefresh) {
          setIsRefreshingBootstrap(false);
        } else {
          setIsLoadingBootstrap(false);
        }
      }
    },
    [tmsID, toast]
  );

  useEffect(() => {
    fetchBootstrap(false).catch(() => {
      // Error already surfaced via toast in fetchBootstrap; avoid unhandled rejection
    });
  }, [fetchBootstrap]);

  const handleBootstrapRefresh = useCallback(() => {
    fetchBootstrap(true).catch(() => {
      // Error already surfaced via toast in fetchBootstrap; avoid unhandled rejection
    });
  }, [fetchBootstrap]);

  const fetchCommodities = useCallback(
    async (customerID?: number, carrierType?: number) => {
      if (!tmsID) return;

      // Always require customerID to avoid fetching commodities for all customers
      if (!customerID) {
        setCommodities(null);
        setIsLoadingCommodities(false);
        currentCommodityRequestRef.current = {};
        return;
      }

      const requestKey = { customerID, carrierType };
      currentCommodityRequestRef.current = requestKey;

      setIsLoadingCommodities(true);

      // Always pass customerID and carrierType to ensure only commodities for this customer and carrier type are fetched
      const res = await getCommodities(
        tmsID,
        false,
        false,
        customerID,
        carrierType
      );

      if (!isMountedRef.current) {
        return; // Return immediately without any state updates
      }

      // Only update state if this request is still the current one (prevents race conditions)
      if (
        currentCommodityRequestRef.current.customerID !==
          requestKey.customerID ||
        currentCommodityRequestRef.current.carrierType !==
          requestKey.carrierType
      ) {
        return; // This request is stale, ignore the response
      }

      if (res.isOk()) {
        setCommodities(res.value.commodityList);
        setIsLoadingCommodities(false);
        return;
      }

      handleCommodityOrLocationFetchError(res.error, 'commodities', toast);

      // On error, clear commodities to avoid showing stale data from a previous customer
      setCommodities([]);
      setIsLoadingCommodities(false);
    },
    [tmsID, toast]
  );

  // Set mounted ref to true on mount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Latest-ref bootstrap: this effect must run only when the TMS integration or
  // the mode changes, but the callbacks it invokes are re-created every render.
  const goToLoadBuildingSuggestionRef = useRef<() => void>(() => {});
  goToLoadBuildingSuggestionRef.current = () => {
    if (
      !clickedSuggestion ||
      clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
    ) {
      goToSuggestion({
        suggestionPipeline: SuggestionPipelines.LoadBuilding,
      });
    }
  };
  const fetchTmsDataRef = useRef<() => void>(() => {});
  fetchTmsDataRef.current = () => {
    void fetchCustomers();
    void fetchLocations();
  };

  useEffect(() => {
    if (isCreateMode) {
      goToLoadBuildingSuggestionRef.current();
    }

    if (tmsID) {
      // Commodities will be fetched when customer is selected
      fetchTmsDataRef.current();
    }
  }, [tmsID, isCreateMode]);

  useEffect(() => {
    if (
      clickedSuggestion &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      setIsLoadBuildingSuggestionClicked(true);
      setShowAIHints(true);
    } else {
      setIsLoadBuildingSuggestionClicked(false);
      setShowAIHints(false);
    }
  }, [clickedSuggestion]);

  // Only clear the "Load Created" banner when switching suggestions;
  // keep it when the same suggestion is marked Accepted after building
  useEffect(() => {
    setBuiltLoad(null);
  }, [clickedSuggestion?.id]);

  const editModeDefaultValues = useMemo<NormalizedLoad | undefined>(() => {
    if (!isCreateMode && load) {
      const normalizedLoad = normalizeLoad(tmsName, load);
      return {
        ...normalizedLoad,
        commodities: mapCommodityDefaults(normalizedLoad.commodities ?? []),
        rateData: {
          ...normalizedLoad.rateData,
          // GTZ UpdateOrder API does not use rate; site shows 0, so we match
          rate: 0,
          // Ensure GTZ-only fields exist as null when absent from the load payload,
          // otherwise empty number inputs register as dirty on open.
          carrierCost: normalizedLoad.rateData?.carrierCost ?? null,
          priceToBeat: normalizedLoad.rateData?.priceToBeat ?? null,
          miles: normalizedLoad.rateData?.miles ?? null,
        },
      };
    }

    return undefined;
  }, [isCreateMode, load, tmsName]);

  const createModeSuggestionId = isCreateMode
    ? (clickedSuggestion?.id ?? null)
    : null;

  // Read the suggestion payload through a ref so the defaults below are only
  // re-derived when the create-mode suggestion id changes, never on the
  // transient clickedSuggestion identity churn from extension messages.
  const clickedSuggestionRef = useRef(clickedSuggestion);
  clickedSuggestionRef.current = clickedSuggestion;

  const memoizedDefaultValues: NormalizedLoad = useMemo(() => {
    if (editModeDefaultValues) {
      return editModeDefaultValues;
    }

    // Create mode: check for suggestions or use init data
    if (isCreateMode) {
      if (builtLoad) {
        const normalizedBuilt = normalizeLoad(tmsName, builtLoad);
        return {
          ...normalizedBuilt,
          commodities: mapCommodityDefaults(normalizedBuilt.commodities ?? []),
        };
      }

      const activeSuggestion =
        createModeSuggestionId == null ? null : clickedSuggestionRef.current;
      if (
        activeSuggestion &&
        activeSuggestion.pipeline === SuggestionPipelines.LoadBuilding
      ) {
        const initForm = initLoadBuildingForm(tmsName);
        const loadSuggestion = activeSuggestion.suggested as SuggestedLoad;

        // Map and filter line items to match with GTZ accessorials
        // We remove any accessorials that are not in the GTZ accessorials array
        // and add the accessorialId to the line item
        const mapAndFilterLineItems = (items: CostLineItem[]) => {
          if (!accessorials || accessorials.length === 0) return [];
          const mappedAccessorials: (CostLineItem | null)[] = items.map(
            (item) => {
              const match = accessorials.find(
                (a) =>
                  a.displayText.toLowerCase() ===
                  (item.label || '').toLowerCase()
              );
              return match
                ? { ...item, accessorialId: match.accessorialId }
                : null;
            }
          );

          const cleanedMappedAccessorials = mappedAccessorials.filter(
            (item): item is CostLineItem => item !== null
          );

          if (cleanedMappedAccessorials.length === 0) return [];

          const hasBaseRate = cleanedMappedAccessorials.some(
            (item) => item.accessorialId === 1
          );
          if (!hasBaseRate) return []; // Fallback to initForm

          // Ensure base rate is at index 0
          const baseRateIndex = cleanedMappedAccessorials.findIndex(
            (item) => item.accessorialId === 1
          );
          if (baseRateIndex > 0) {
            const baseRateItem = cleanedMappedAccessorials.splice(
              baseRateIndex,
              1
            )[0];
            cleanedMappedAccessorials.unshift(baseRateItem);
          }

          return cleanedMappedAccessorials;
        };

        const customerLineItemsArray: CostLineItem[] = mapAndFilterLineItems(
          normalizeToArray(loadSuggestion.rateData?.customerLineItems) || []
        );
        const carrierLineItemsArray: CostLineItem[] = mapAndFilterLineItems(
          normalizeToArray(loadSuggestion.rateData?.carrierLineItems) || []
        );

        // Convert object-with-numeric-keys back to arrays (API response may return objects)
        const commoditiesArray: Commodity[] =
          normalizeToArray(loadSuggestion.commodities) || [];
        const additionalReferencesArray: AdditionalReference[] =
          normalizeToArray(loadSuggestion.additionalReferences) || [];
        const notesArray: Note[] = normalizeToArray(loadSuggestion.notes) || [];

        const defaultPickupTimes = getDefaultTimes(LoadStopTypes.Pickup);
        const defaultConsigneeTimes = getDefaultTimes(LoadStopTypes.Consignee);
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const tomorrowISO = tomorrow.toISOString();

        const suggestedFields: Load = {
          ...createInitLoad(),
          ...loadSuggestion,
          mode: initForm.mode, // GlobalTranz TL form requires TL mode
          commodities: commoditiesArray,
          additionalReferences: additionalReferencesArray,
          notes: notesArray,
          customer: loadSuggestion.customer
            ? {
                ...loadSuggestion.customer,
                refNumberCandidates: normalizeToStringArray(
                  loadSuggestion.customer.refNumberCandidates
                ),
              }
            : loadSuggestion.customer,
          pickup: loadSuggestion.pickup
            ? {
                ...loadSuggestion.pickup,
                apptStartTime: loadSuggestion.pickup.apptStartTime
                  ? new Date(loadSuggestion.pickup.apptStartTime).toISOString()
                  : null,
                apptEndTime: loadSuggestion.pickup.apptEndTime
                  ? new Date(loadSuggestion.pickup.apptEndTime).toISOString()
                  : null,
                readyTime: loadSuggestion.pickup.readyTime
                  ? new Date(loadSuggestion.pickup.readyTime).toISOString()
                  : tomorrowISO,
                // MGB-only planned end - serialize Date -> string for Load wire type
                readyTimeEnd: loadSuggestion.pickup.readyTimeEnd
                  ? new Date(loadSuggestion.pickup.readyTimeEnd).toISOString()
                  : undefined,
                refNumberCandidates: normalizeToStringArray(
                  loadSuggestion.pickup.refNumberCandidates
                ),
                openTime: loadSuggestion.pickup.openTime
                  ? toHHMMString(loadSuggestion.pickup.openTime)
                  : defaultPickupTimes.openTime,
                closeTime: loadSuggestion.pickup.closeTime
                  ? toHHMMString(loadSuggestion.pickup.closeTime)
                  : defaultPickupTimes.closeTime,
              }
            : ({
                ...createInitLoad().pickup,
                readyTime: tomorrowISO,
                openTime: defaultPickupTimes.openTime,
                closeTime: defaultPickupTimes.closeTime,
              } as Pickup),
          consignee: loadSuggestion.consignee
            ? {
                ...loadSuggestion.consignee,
                apptStartTime: loadSuggestion.consignee.apptStartTime
                  ? new Date(
                      loadSuggestion.consignee.apptStartTime
                    ).toISOString()
                  : null,
                apptEndTime: loadSuggestion.consignee.apptEndTime
                  ? new Date(loadSuggestion.consignee.apptEndTime).toISOString()
                  : null,
                mustDeliver: loadSuggestion.consignee.mustDeliver
                  ? new Date(loadSuggestion.consignee.mustDeliver).toISOString()
                  : tomorrowISO,
                // MGB-only planned end - serialize Date -> string for Load wire type
                mustDeliverEnd: loadSuggestion.consignee.mustDeliverEnd
                  ? new Date(
                      loadSuggestion.consignee.mustDeliverEnd
                    ).toISOString()
                  : undefined,
                refNumberCandidates: normalizeToStringArray(
                  loadSuggestion.consignee.refNumberCandidates
                ),
                openTime: loadSuggestion.consignee.openTime
                  ? toHHMMString(loadSuggestion.consignee.openTime)
                  : defaultConsigneeTimes.openTime,
                closeTime: loadSuggestion.consignee.closeTime
                  ? toHHMMString(loadSuggestion.consignee.closeTime)
                  : defaultConsigneeTimes.closeTime,
              }
            : ({
                ...createInitLoad().consignee,
                mustDeliver: tomorrowISO,
                openTime: defaultConsigneeTimes.openTime,
                closeTime: defaultConsigneeTimes.closeTime,
              } as Dropoff),
          specifications: {
            ...createInitSpecs(),
            ...loadSuggestion.specifications,
            // No equipment in the email: leave it empty (not the blank
            // form's Van) so the rep picks one.
            transportType: normalizeGlobalTranzTransportType(
              suggestedTransportTypeOrEmpty(loadSuggestion.specifications),
              equipmentTypes
            ),
            serviceType: initForm.specifications.serviceType, // GlobalTranz TL form requires TL service type
            service: ['1', '2', '4'].includes(
              String(loadSuggestion.specifications?.service)
            )
              ? String(loadSuggestion.specifications?.service)
              : initForm.specifications.service,
            shipmentType:
              loadSuggestion.specifications?.shipmentType || 'Spot Quote',
          },
          rateData: {
            ...createInitRateData(),
            ...loadSuggestion.rateData,
            customerLineItems:
              customerLineItemsArray.length > 0
                ? customerLineItemsArray
                : initForm.rateData.customerLineItems,
            carrierLineItems:
              carrierLineItemsArray.length > 0
                ? carrierLineItemsArray
                : initForm.rateData.carrierLineItems,
          },
        };

        const merged = {
          ...initForm,
          ...normalizeLoad(tmsName, suggestedFields),
          mode: initForm.mode, // GlobalTranz TL form requires TL mode
          declaredValueUSD: initForm.declaredValueUSD,
        } as NormalizedLoad;

        return {
          ...merged,
          commodities: mapCommodityDefaults(merged.commodities ?? []),
        };
      }

      return initLoadBuildingForm(tmsName);
    }

    return initLoadBuildingForm(tmsName);
  }, [
    createModeSuggestionId,
    tmsName,
    builtLoad,
    isCreateMode,
    editModeDefaultValues,
    equipmentTypes,
    accessorials,
  ]);

  const formMethods = useForm<NormalizedLoad>({
    defaultValues: memoizedDefaultValues,
  });

  const { handleSubmit } = formMethods;

  // Create-mode-only Load Building suggestion id. Edit-mode Load View suggestion
  // clicks must not retrigger the reset below, or in-progress edits get wiped.
  const createModeLoadBuildingSuggestionId =
    isCreateMode &&
    clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
      ? clickedSuggestion.id
      : null;

  // Reset form when default values change
  useEffect(() => {
    formMethods.reset(memoizedDefaultValues, {
      keepDefaultValues: false,
      keepTouched: false,
      keepDirtyValues: false,
      keepDirty: false,
    });

    return scheduleLoadBuildingSuggestionValidation(
      createModeLoadBuildingSuggestionId != null && !builtLoad,
      () => {
        void formMethods.trigger();
      }
    );
  }, [
    memoizedDefaultValues,
    createModeLoadBuildingSuggestionId,
    builtLoad,
    formMethods,
  ]);

  useApplyLoadViewSuggestion(
    formMethods.setValue,
    clickedSuggestion,
    load?.freightTrackingID,
    !isCreateMode,
    memoizedDefaultValues
  );

  useFocusLoadViewSuggestion(
    clickedSuggestion,
    load?.freightTrackingID,
    setActiveTabs,
    !isCreateMode,
    Object.values(AvailableTabs)
  );

  // Inject the selected customer into the customers array so refresh (commodities/locations) works in view mode.
  // Use load.customer.id (DB id from API) when present so getLocations/getCommodities use the correct ID; else fallback to externalTMSID for backward compat.
  // Re-run when customers list updates (e.g. after fetch) so load customer stays in the list.
  const defaultCustomer = memoizedDefaultValues?.customer as Undef<
    NormalizedLoad['customer'] & { id?: number }
  >;
  const defaultPickup = memoizedDefaultValues?.pickup;
  const defaultConsignee = memoizedDefaultValues?.consignee;

  useEffect(() => {
    if (!defaultCustomer?.externalTMSID || isLoadingCustomers) {
      return;
    }

    const customerWithId = {
      ...defaultCustomer,
      ID:
        defaultCustomer.id ??
        (Number(defaultCustomer.externalTMSID) || undefined),
    } as TMSCustomer & { ID?: number };

    const alreadyInList = (customers ?? []).some(
      (c) => c.externalTMSID === customerWithId.externalTMSID
    );
    if (alreadyInList) {
      return;
    }

    setCustomers((prevCustomers) =>
      injectSelectedObject(customerWithId, prevCustomers ?? [])
    );
    // Re-inject when the customers list changes (e.g. after fetch) so the load
    // customer is not lost; `injectSelectedObject` returns the same array when
    // the customer is already present, so this cannot loop.
  }, [defaultCustomer, customers, isLoadingCustomers]);

  // Inject locations
  useEffect(() => {
    if (isLoadingLocations || !locations) {
      return;
    }

    const additionalLocations: TMSLocation[] = [];
    if (defaultPickup?.externalTMSID) {
      additionalLocations.push(defaultPickup);
    }
    if (defaultConsignee?.externalTMSID) {
      additionalLocations.push(defaultConsignee);
    }

    if (additionalLocations.length > 0) {
      setLocations((prevLocations) => {
        let updatedLocations = prevLocations ?? [];
        additionalLocations.forEach((loc) => {
          updatedLocations = injectSelectedObject(loc, updatedLocations);
        });
        return updatedLocations;
      });
    }
  }, [isLoadingLocations, locations, defaultPickup, defaultConsignee]);

  // Hook to cache load building form
  useEffect(() => {
    if (isCreateMode) {
      const subscription = formMethods.watch((value) => {
        FormStorageService.saveFormState(`${tmsName}_${threadId}`, {
          threadID: threadId,
          values: value,
          clickedSuggestion: isLoadBuildingSuggestionClicked
            ? clickedSuggestion
            : null,
          dirtyFields: formMethods.formState.dirtyFields,
        });
      });
      return () => subscription.unsubscribe();
    }
    return undefined;
  }, [
    formMethods,
    threadId,
    isLoadBuildingSuggestionClicked,
    clickedSuggestion,
    tmsName,
    isCreateMode,
  ]);

  const handleClearForm = () => {
    if (isCreateMode) {
      setShowAIHints(false);
      setBuiltLoad(null);
      FormStorageService.clearFormState(`${tmsName}_${threadId}`);

      setCurrentState((prevState) => ({
        ...prevState,
        clickedSuggestion: null,
      }));

      const emptyForm = initLoadBuildingForm(tmsName);
      formMethods.reset(emptyForm, {
        keepDefaultValues: false,
        keepDirty: false,
        keepDirtyValues: false,
        keepTouched: false,
      });

      formMethods.clearErrors();
    }
  };

  const onInvalid: SubmitErrorHandler<NormalizedLoad> = async (errors) => {
    handleFormErrors(errors, {
      tmsName: TMS.GlobalTranzTMS,
      maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
      activeTabs,
      setActiveTabs,
      toast,
    });
  };

  const onSubmitCreate: SubmitHandler<NormalizedLoad> = async (_data) => {
    setIsLoading(true);

    const updatedData = withCommodityTotalWeight(formMethods.getValues());

    const applySubmitValidationErrors = (
      fieldErrors: ManualFieldError<NormalizedLoad>[]
    ) =>
      applyManualValidationErrors(
        formMethods,
        fieldErrors,
        loadBuildingManualValidationDisplayOptions({
          tmsName: TMS.GlobalTranzTMS,
          activeTabs,
          setActiveTabs,
          toast,
          afterErrorHandled: () => setIsLoading(false),
        })
      );

    formMethods.clearErrors();

    const duplicateStops = validateDuplicateStopLocations(
      updatedData.pickup?.externalTMSID,
      updatedData.consignee?.externalTMSID
    );
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        { path: 'pickup.externalTMSID', message: duplicateStops.message },
        { path: 'consignee.externalTMSID', message: duplicateStops.message },
      ]);
      return;
    }

    // Validate: Delivery date must be >= pickup date
    const pickupDeliveryDates = validateDeliveryOnOrAfterPickupDate(
      updatedData.pickup?.readyTime,
      updatedData.consignee?.mustDeliver
    );
    if (!pickupDeliveryDates.ok) {
      applySubmitValidationErrors([
        { path: 'consignee.mustDeliver', message: pickupDeliveryDates.message },
      ]);
      return;
    }

    // Validate: At least 1 commodity is required
    if (!updatedData.commodities || updatedData.commodities.length === 0) {
      toast({
        description: 'Please add at least one shipment item.',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    // Validate: Each commodity must have description and weight
    const invalidCommodities = updatedData.commodities.filter(
      (commodity) =>
        !commodity.description?.trim() ||
        !commodity.weightTotal ||
        commodity.weightTotal <= 0
    );

    if (invalidCommodities.length > 0) {
      toast({
        description:
          'Please ensure all shipment items have a description and weight.',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    // Validate: At least 1 rate item is required
    const customerLineItems = updatedData.rateData?.customerLineItems || [];
    const carrierLineItems = updatedData.rateData?.carrierLineItems || [];

    if (customerLineItems.length === 0 || carrierLineItems.length === 0) {
      toast({
        description:
          'Please add at least one rate item with both Cost and Revenue.',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    // Validate: Each rate item must have Revenue filled; Cost defaults to 0 when empty
    const invalidRateItems = customerLineItems.filter((_, index) => {
      const customerItem = customerLineItems[index];
      const carrierItem = carrierLineItems[index];
      const revenue = customerItem?.totalChargeUSD;
      const cost = carrierItem?.totalChargeUSD;
      const normalizedCost = cost == null || Number.isNaN(cost) ? 0 : cost;
      return !revenue || revenue <= 0 || normalizedCost < 0;
    });

    if (invalidRateItems.length > 0) {
      toast({
        description:
          'Please ensure all rate items have Revenue filled (greater than 0).',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    const normalizedCarrierLineItems = carrierLineItems.map((item) => ({
      ...item,
      totalChargeUSD:
        item.totalChargeUSD == null || Number.isNaN(item.totalChargeUSD)
          ? 0
          : item.totalChargeUSD,
    }));

    const notesArray: Note[] = [];

    if (updatedData.pickup?.instructions?.trim()) {
      notesArray.push(
        createNoteFromInstructions(updatedData.pickup.instructions)
      );
    }

    const reqData = withLoadBuildingSuggestionContext(
      { clickedSuggestion, threadId, emailId, emailExternalId: threadItemId },
      {
        load: {
          ...updatedData,
          freightTrackingID: '',
          externalTMSID: '',
          tmsID: tmsID ?? 0,
          operator: updatedData.operator,
          mode: updatedData.mode,
          carrier: updatedData.carrier as LoadCarrier,
          customer: denormalizeDatesForTMSForm(
            tmsName,
            updatedData.customer
          ) as Customer,
          billTo: denormalizeDatesForTMSForm(
            tmsName,
            updatedData.billTo
          ) as CompanyCoreInfo,
          specifications: {
            ...updatedData.specifications,
          },
          pickup: (() => {
            const denormalized = denormalizeDatesForTMSForm(
              tmsName,
              updatedData.pickup
            ) as Pickup;
            return {
              ...denormalized,
              openTime: toHHMMString(updatedData.pickup?.openTime),
              closeTime: toHHMMString(updatedData.pickup?.closeTime),
            } as Pickup;
          })(),
          consignee: (() => {
            const denormalized = denormalizeDatesForTMSForm(
              tmsName,
              updatedData.consignee
            ) as Dropoff;
            return {
              ...denormalized,
              openTime: toHHMMString(updatedData.consignee?.openTime),
              closeTime: toHHMMString(updatedData.consignee?.closeTime),
            } as Dropoff;
          })(),
          rateData: {
            ...updatedData.rateData,
            carrierLineItems: normalizedCarrierLineItems,
          },
          notes: notesArray,
        },
      }
    );

    const res = await guardedCreateLoad(
      updatedData.customer,
      reqData,
      customers
    );
    if (!res) {
      setIsLoading(false);
      return;
    }

    if (res.isOk()) {
      setBuiltLoad(res.value.load);
      FormStorageService.clearFormState(`${tmsName}_${threadId}`);
      if (
        clickedSuggestion &&
        clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
      ) {
        setCurrentState((prevState) => ({
          ...prevState,
          ...applyCompleteKitSuggestions(
            prevState,
            clickedSuggestion.id,
            SuggestionPipelines.LoadBuilding
          ),
        }));
      }
      onSuccess?.(res.value);
      setIsLoading(false);
      return;
    }
    toast({
      description: res.error.message || 'Failed to create load.',
      variant: 'destructive',
    });
    onError?.(res.error);
    setIsLoading(false);
  };

  const onSubmitEdit: SubmitHandler<NormalizedLoad> = async () => {
    if (!load) return;

    setIsLoading(true);

    const updatedData = withCommodityTotalWeight(formMethods.getValues());

    const applySubmitValidationErrors = (
      fieldErrors: ManualFieldError<NormalizedLoad>[]
    ) =>
      applyManualValidationErrors(
        formMethods,
        fieldErrors,
        loadBuildingManualValidationDisplayOptions({
          tmsName: TMS.GlobalTranzTMS,
          activeTabs,
          setActiveTabs,
          toast,
          afterErrorHandled: () => setIsLoading(false),
        })
      );

    formMethods.clearErrors();

    const duplicateStops = validateDuplicateStopLocations(
      updatedData.pickup?.externalTMSID,
      updatedData.consignee?.externalTMSID
    );
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        { path: 'pickup.externalTMSID', message: duplicateStops.message },
        { path: 'consignee.externalTMSID', message: duplicateStops.message },
      ]);
      return;
    }

    // Validate: Delivery date must be >= pickup date
    const pickupDeliveryDates = validateDeliveryOnOrAfterPickupDate(
      updatedData.pickup?.readyTime,
      updatedData.consignee?.mustDeliver
    );
    if (!pickupDeliveryDates.ok) {
      applySubmitValidationErrors([
        { path: 'consignee.mustDeliver', message: pickupDeliveryDates.message },
      ]);
      return;
    }

    // Use form notes when explicitly set via onNotesChanged (including [] after
    // all notes are deleted). Only fall back when notes were never touched so
    // that deleting the last note does not revert to the original load.notes.
    let notesForPayload: Note[];
    if (updatedData.notes !== undefined) {
      notesForPayload = updatedData.notes;
    } else if (updatedData.pickup?.instructions?.trim()) {
      notesForPayload = [
        createNoteFromInstructions(updatedData.pickup.instructions),
      ];
    } else {
      notesForPayload = load.notes ?? [];
    }

    // Include form-edited commodities and declared value in the load so the update persists them
    const commoditiesForApi = (updatedData.commodities ?? []).map((c) => ({
      ...c,
      // BE expects dimensionsUnit; form uses dimensionUnit
      dimensionsUnit:
        c.dimensionUnit ??
        (c as Record<string, unknown>).dimensionsUnit ??
        'inches',
    }));

    const reqData = {
      operation: TMSOperation.UpdateLoad,
      load: {
        ...load,
        status: updatedData.status ?? load.status,
        freightTrackingID: updatedData.freightTrackingID,
        externalTMSID: updatedData.externalTMSID,
        operator: updatedData.operator,
        poNums: updatedData.poNums ?? load.poNums,
        additionalReferences:
          updatedData.additionalReferences ?? load.additionalReferences,
        customer: denormalizeDatesForTMSForm(
          tmsName,
          updatedData.customer
        ) as Customer,
        billTo: denormalizeDatesForTMSForm(
          tmsName,
          updatedData.billTo
        ) as CompanyCoreInfo,
        specifications: denormalizeDatesForTMSForm(
          tmsName,
          updatedData.specifications
        ),
        pickup: (() => {
          const p = denormalizeDatesForTMSForm(
            tmsName,
            updatedData.pickup
          ) as Pickup;
          const hasApptNum =
            (updatedData.pickup?.apptNum ?? '').toString().trim() !== '';
          const confirmedWithLoc =
            updatedData.pickup?.apptConfirmedWithLocation === true;
          return {
            ...p,
            externalTMSID:
              updatedData.pickup.externalTMSID ?? updatedData.pickup.name,
            // Dock hours (open/close) for UpdateOrder come from pickup.openTime/closeTime (HH:MM).
            openTime: toHHMMString(updatedData.pickup?.openTime),
            closeTime: toHHMMString(updatedData.pickup?.closeTime),
            apptConfirmed: !!(
              p.apptConfirmed ||
              confirmedWithLoc ||
              hasApptNum
            ),
          } as Pickup;
        })(),
        consignee: (() => {
          const c = denormalizeDatesForTMSForm(
            tmsName,
            updatedData.consignee
          ) as Dropoff;
          const hasApptNum =
            (updatedData.consignee?.apptNum ?? '').toString().trim() !== '';
          const confirmedWithLoc =
            updatedData.consignee?.apptConfirmedWithLocation === true;
          return {
            ...c,
            externalTMSID:
              updatedData.consignee.externalTMSID ?? updatedData.consignee.name,
            // Dock hours (open/close) for UpdateOrder come from consignee.openTime/closeTime (HH:MM).
            openTime: toHHMMString(updatedData.consignee?.openTime),
            closeTime: toHHMMString(updatedData.consignee?.closeTime),
            apptConfirmed: !!(
              c.apptConfirmed ||
              confirmedWithLoc ||
              hasApptNum
            ),
          } as Dropoff;
        })(),
        carrier: {
          ...denormalizeDatesForTMSForm(tmsName, updatedData.carrier),
          rateConfirmationSent: !!updatedData.carrier.rateConfirmationSent,
        } as LoadCarrier,
        rateData: denormalizeDatesForTMSForm(
          tmsName,
          updatedData.rateData
        ) as RateData,
        commodities: commoditiesForApi,
        declaredValueUSD: updatedData.declaredValueUSD ?? load.declaredValueUSD,
        notes: notesForPayload,
        accessorials: (updatedData.accessorials ?? []).map((acc) => ({
          externalId: acc.id,
          name: acc.displayText ?? '',
          code: '',
          ...(acc.value != null && acc.value !== ''
            ? { value: acc.value }
            : {}),
        })),
      },
    };

    const res = await updateTMS(load.ID!, reqData);

    const isSuccess = res.isOk();
    const payload = isSuccess ? res.value : res.error;

    toast({
      description: payload.message,
      variant: isSuccess ? 'success' : 'destructive',
    });

    if (isSuccess) {
      await acceptLoadViewSuggestion(
        clickedSuggestion,
        reqData.load,
        setCurrentState,
        undefined,
        tmsName
      );
    }

    if (payload.load) {
      formMethods.reset(normalizeLoad(tmsName, payload.load), {
        keepDefaultValues: false,
        keepDirty: false,
        keepDirtyValues: false,
        keepTouched: false,
      });
    }

    if (payload.load && payload.loadAttributes) {
      await invalidateLoad(payload.load, payload.loadAttributes);
    } else {
      await invalidateLoad();
    }

    if (isSuccess) {
      onSuccess?.(res.value);
    } else {
      onError?.(res.error);
    }

    setIsLoading(false);
  };

  const handleFormSubmit: SubmitHandler<NormalizedLoad> = async (data) => {
    if (onSubmit) {
      onSubmit(data);
    } else if (isCreateMode) {
      await onSubmitCreate(data);
    } else {
      await onSubmitEdit(data);
    }
  };

  // Watch customer to get customerBK for shipment items
  const customerBK = useWatch({
    control: formMethods.control,
    name: 'customer.externalTMSID',
  });

  const serviceType = useWatch({
    control: formMethods.control,
    name: 'specifications.serviceType',
  });

  // Find customer object to get database ID for shipment items
  const selectedCustomer = customers?.find(
    (c) => c.externalTMSID === customerBK
  );
  // API response includes ID field from gorm.Model (uppercase), but TypeScript type doesn't include it
  const customerID = (selectedCustomer as any)?.ID as Undef<number>;

  // GlobalTranz only: auto-select the first customer from the original list in create mode
  // This ensures we always have a valid customer selection (and customerID) even when
  // the user starts by searching, which currently relies on an initial selection.
  useEffect(() => {
    if (isCreateMode && !customerBK && customers && customers.length > 0) {
      formMethods.setValue(
        'customer.externalTMSID',
        customers[0].externalTMSID,
        {
          shouldDirty: true,
        }
      );
    }
  }, [isCreateMode, customerBK, customers, formMethods]);

  // Reads from the backend DB (forceRefresh=false). Used on mount and whenever the customer
  // selection changes to populate the location dropdowns from the cached RDS data.
  //
  // clearOnError (default true): clears the location list on failure so stale data from a
  // previous customer is never shown. Pass false when calling after a GTZ address book import -
  // the sync has already written to RDS so a transient follow-up error shouldn't wipe options
  // the user can still use.
  const fetchLocations = useCallback(
    async (clearOnError = true) => {
      if (!tmsID) return;

      const requestCustomerExternalTmsId = customerBK || undefined;
      currentRequestLocationCustomerExternalTmsIdRef.current =
        requestCustomerExternalTmsId;

      setIsLoadingLocations(true);

      const res = await getLocations(
        tmsID,
        false,
        false,
        requestCustomerExternalTmsId,
        undefined
      );

      if (!isMountedRef.current) {
        return; // Return immediately without any state updates
      }

      // Only update state if this request is still the current one (prevents race conditions)
      if (
        currentRequestLocationCustomerExternalTmsIdRef.current !==
        requestCustomerExternalTmsId
      ) {
        return; // This request is stale, ignore the response
      }

      if (res.isOk()) {
        setLocations(res.value.locationList);
        setIsLoadingLocations(false);
        return;
      }

      handleCommodityOrLocationFetchError(res.error, 'locations', toast, {
        isManualRefresh: !clearOnError,
      });

      if (clearOnError) {
        setLocations([]);
      }
      setIsLoadingLocations(false);
    },
    [tmsID, customerBK, toast]
  );

  // Bypasses the backend cache and fetches directly from the GTZ live API (forceRefresh=true).
  // Triggered by the manual "Refresh Locations" button. Because it hits GTZ directly, it will
  // NOT reflect locations synced via the address book import - those are written to RDS and
  // only visible via fetchLocations (forceRefresh=false). On error the existing list is preserved
  // (no setLocations([])) since this is a user-initiated action and wiping options would be
  // worse than leaving the last-known list in place.
  const handleRefreshLocations = useCallback(async () => {
    if (!tmsID) return;

    setIsLoadingLocations(true);

    const requestCustomerExternalTmsId =
      customerBK && customerBK !== '' ? customerBK : undefined;
    currentRequestLocationCustomerExternalTmsIdRef.current =
      requestCustomerExternalTmsId;

    const res = await getLocations(
      tmsID,
      true,
      false,
      requestCustomerExternalTmsId,
      undefined
    );

    if (!isMountedRef.current) {
      setIsLoadingLocations(false);
      return;
    }

    if (
      currentRequestLocationCustomerExternalTmsIdRef.current !==
      requestCustomerExternalTmsId
    ) {
      setIsLoadingLocations(false);
      return;
    }

    if (res.isOk()) {
      setLocations(res.value.locationList);
      toast({
        description: 'Successfully refreshed location list.',
        variant: 'success',
      });
      setIsLoadingLocations(false);
      return;
    }
    handleCommodityOrLocationFetchError(res.error, 'locations', toast, {
      isManualRefresh: true,
    });
    setIsLoadingLocations(false);
  }, [tmsID, customerBK, toast]);

  // For TL, carrier type is the same as service type (TL = 3 based on GlobalTranz)
  // Map service type to carrier type: 'TL' -> 3
  const carrierType = serviceType === 'TL' ? 3 : undefined;

  // Refetch commodities when customer selection or service type changes.
  // In view/edit mode, only skip when we already have both load.commodities and a populated dropdown options list.
  // In view/edit mode the dropdown is already populated once both the load's
  // commodities and the fetched options exist; in create mode this stays false
  // so a fetched list never retriggers the effect below.
  const hasLoadedEditModeCommodities =
    !isCreateMode && !!load?.commodities?.length && !!commodities?.length;

  useEffect(() => {
    if (hasLoadedEditModeCommodities) {
      setIsLoadingCommodities(false);
      return;
    }
    if (tmsID && customerID && serviceType === 'TL') {
      fetchCommodities(customerID, carrierType);
    } else if (tmsID && customerID) {
      fetchCommodities(customerID, undefined);
    } else if (tmsID && !customerBK) {
      setCommodities(null);
    } else if (tmsID && customerBK && !customerID) {
      // Clear commodities when customerBK is truthy but customerID is not yet available
      // This prevents stale commodity data from being displayed
      setCommodities(null);
    }
  }, [
    hasLoadedEditModeCommodities,
    tmsID,
    customerID,
    customerBK,
    serviceType,
    carrierType,
    fetchCommodities,
  ]);

  // Refetch locations when customer selection changes
  useEffect(() => {
    // Skip on initial mount to avoid duplicate call with the initial fetchLocations() at line 372
    const isInitialMount = prevCustomerBKRef.current === undefined;

    if (tmsID && customerBK) {
      fetchLocations();
    } else if (tmsID && !customerBK && !isInitialMount) {
      // Fetch all locations when customer is cleared (but not on initial mount)
      fetchLocations();
    }

    // Update ref to track previous customerBK value
    prevCustomerBKRef.current = customerBK;

    // Mark request as cancelled when dependencies change
    return () => {
      // When this effect re-runs (due to dependency change), invalidate any in-flight request
      // by setting the ref to a value that won't match any response
      currentRequestLocationCustomerExternalTmsIdRef.current = undefined;
    };
  }, [tmsID, customerBK, fetchLocations]);

  const buttonText = isCreateMode ? ButtonText.BuildLoad : ButtonText.UpdateTMS;
  const buttonNamePosthog = isCreateMode
    ? ButtonNamePosthog.BuildLoad
    : ButtonNamePosthog.UpdateTMS;

  return (
    <Flex direction='col' className='mb-5 w-full'>
      <ExtendedFormProvider
        aiDefaultValues={showAIHints}
        aiIconOnly={true}
        highlightDirtyFields={!isCreateMode}
      >
        <FormProvider {...formMethods}>
          <form
            onSubmit={handleSubmit(handleFormSubmit, onInvalid)}
            className='w-full'
          >
            <LoadBuildingAccordionControls
              activeTabs={activeTabs}
              allTabs={allTabs}
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
              defaultView={defaultView}
              onDefaultViewChange={setDefaultView}
            />
            <Accordion
              type='multiple'
              value={activeTabs}
              onValueChange={setActiveTabs}
              className='w-full'
            >
              <LoadSectionAccordionItem
                label='Customer'
                icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.customer}
                activeTabs={activeTabs}
              >
                <CustomerSectionForm
                  formMethods={formMethods}
                  customers={customers}
                  setCustomers={setCustomers}
                  isLoadingCustomers={isLoadingCustomers}
                  setIsLoadingCustomers={setIsLoadingCustomers}
                  originalSuggestionData={
                    clickedSuggestion?.suggested as NormalizedLoad
                  }
                  isCreateMode={isCreateMode}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Specs'
                icon={<Weight className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.specifications}
                activeTabs={activeTabs}
              >
                <SpecificationsForm
                  formMethods={formMethods}
                  isCreateMode={isCreateMode}
                  tmsID={tmsID}
                  equipmentTypes={equipmentTypes}
                  equipmentMapping={equipmentMapping}
                  equipmentLengths={equipmentLengths}
                  displayFields={displayFields}
                  onBootstrapRefresh={handleBootstrapRefresh}
                  isRefreshingBootstrap={isRefreshingBootstrap}
                  isLoadingBootstrap={_isLoadingBootstrap}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Shipment Items'
                icon={<Package className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.shipmentItems}
                activeTabs={activeTabs}
              >
                <ShipmentItems
                  formMethods={formMethods}
                  commodities={commodities}
                  setCommodities={setCommodities}
                  isLoadingCommodities={isLoadingCommodities}
                  setIsLoadingCommodities={setIsLoadingCommodities}
                  tmsID={tmsID}
                  customerID={customerID}
                  customerBK={customerBK}
                  carrierType={carrierType}
                  initialCustomers={customers}
                  initialIsLoadingCustomers={isLoadingCustomers}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Rates'
                icon={
                  <CircleDollarSignIcon className='h-6 w-6' strokeWidth={1} />
                }
                name={AvailableTabs.rates}
                activeTabs={activeTabs}
              >
                <FinancialsForm
                  formMethods={formMethods}
                  tmsID={tmsID}
                  accessorials={accessorials}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Pickup'
                icon={<BoxIcon className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.pickup}
                activeTabs={activeTabs}
              >
                <StopForm
                  stop='pickup'
                  formMethods={formMethods}
                  isLoadingLocations={isLoadingLocations}
                  locations={locations}
                  handleRefreshLocations={handleRefreshLocations}
                  originalSuggestionData={
                    clickedSuggestion?.suggested as NormalizedLoad
                  }
                  tmsID={tmsID}
                  customerID={customerID}
                  setLocations={setLocations}
                  initialCustomers={customers}
                  initialIsLoadingCustomers={isLoadingCustomers}
                  apptConfirmedWithLocationFromLoad={
                    !isCreateMode &&
                    (load?.pickup?.apptConfirmed ??
                      load?.pickup?.apptConfirmedWithLocation) === true
                  }
                  fetchLocations={() => fetchLocations(false)}
                  isCreateMode={isCreateMode}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Consignee'
                icon={<WarehouseIcon className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.consignee}
                activeTabs={activeTabs}
              >
                <StopForm
                  stop='consignee'
                  formMethods={formMethods}
                  isLoadingLocations={isLoadingLocations}
                  locations={locations}
                  handleRefreshLocations={handleRefreshLocations}
                  originalSuggestionData={
                    clickedSuggestion?.suggested as NormalizedLoad
                  }
                  tmsID={tmsID}
                  customerID={customerID}
                  setLocations={setLocations}
                  initialCustomers={customers}
                  initialIsLoadingCustomers={isLoadingCustomers}
                  apptConfirmedWithLocationFromLoad={
                    !isCreateMode &&
                    (load?.consignee?.apptConfirmed ??
                      load?.consignee?.apptConfirmedWithLocation) === true
                  }
                  fetchLocations={() => fetchLocations(false)}
                  isCreateMode={isCreateMode}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Accessorials'
                icon={<Package className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.accessorials}
                activeTabs={activeTabs}
              >
                <AccessorialsForm
                  formMethods={formMethods}
                  tmsID={tmsID}
                  equipmentMapping={equipmentMapping}
                  equipmentTypes={equipmentTypes}
                  accessorials={accessorials}
                  displayFields={displayFields}
                  isBootstrapLoading={
                    _isLoadingBootstrap || isRefreshingBootstrap
                  }
                />
              </LoadSectionAccordionItem>

              {!isCreateMode && load && tmsID && (
                <LoadSectionAccordionItem
                  label='Notes'
                  icon={<NotebookIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.notes}
                  activeTabs={activeTabs}
                >
                  <NotesSectionForm
                    key={load.ID}
                    load={load}
                    tmsID={tmsID}
                    onNotesChanged={(notes) => {
                      formMethods.setValue('notes', notes, {
                        shouldDirty: false,
                      });
                    }}
                  />
                </LoadSectionAccordionItem>
              )}
            </Accordion>

            <Flex direction='col' className='w-full mt-4'>
              <Button
                buttonNamePosthog={buttonNamePosthog}
                type='submit'
                className='w-full'
                disabled={isLoading}
                data-testid={
                  isCreateMode
                    ? 'create-load-submit'
                    : 'load-info-update-submit'
                }
                logProperties={
                  !isCreateMode && load
                    ? {
                        loadID: load.ID,
                        freightTrackingID: load.freightTrackingID,
                        serviceID: load.serviceID,
                      }
                    : undefined
                }
              >
                {isLoading ? <ButtonLoader /> : buttonText}
              </Button>

              {isCreateMode && formMethods.formState.isDirty && (
                <Flex
                  direction='row'
                  justify='center'
                  align='center'
                  className='w-full'
                >
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.ClearForm}
                    type='button'
                    className='mt-4 h-8 text-sm text-neutral-700'
                    disabled={isLoading}
                    variant='outline'
                    onClick={handleClearForm}
                  >
                    {ButtonText.ClearForm}
                  </Button>
                </Flex>
              )}

              {isCreateMode && builtLoad?.externalTMSID && (
                <LoadCreatedMessage builtLoadId={builtLoad.externalTMSID} />
              )}
            </Flex>
          </form>
        </FormProvider>
      </ExtendedFormProvider>
    </Flex>
  );
}
